import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TableContainer, BodyWrapper } from "assets/css/globle";
import DownloadIcon from "assets/svgIcons/DownloadIcon";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "assets/svgIcons/AccordionExpandMoreIcon";
import { DataGrid } from "@mui/x-data-grid";
import { Loader } from "commonComponents/Loader/Loader";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "assets/svgIcons/CloseIcon";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { deviceType, productLevel } from "constant";
import { COMMON_SERVICES } from "redux/reduxToolkitServices/_commonServices/commonServices";
import { useDispatch, useSelector } from "react-redux";
import {
  AllSiteSettingsContainer,
  Button,
  Heading,
  WorkSection,
  ContentArea,
  BlueAccordionStyled,
  FilterButtonGroup,
  FilterButton,
  DeleteToggleButton,
  SearchFilterContainer,
  SearchInput,
  ClearButton,
  ProductLevelDropdownContainer,
  ProductLevelButton,
  ProductLevelDropdown,
  ProductLevelOption,
  ClearAllButton,
  ProductLevelClearButton,
  EditButton,
  ModalBox,
  ModalHeader,
  ModalContent,
  ModalSection,
  SectionTitle,
  SectionContent,
  FormRow,
  FormField,
  Label,
  InfoRow,
  NumberInput,
  ModalFooter,
  ModalButton,
} from "./styles";

// Helper Functions
const compareVersions = (a, b) => {
  // Handle null/undefined/non-string values
  if (!a || typeof a !== 'string') return 1;
  if (!b || typeof b !== 'string') return -1;

  const aParts = a.split('.').map(Number);
  const bParts = b.split('.').map(Number);

  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aVal = aParts[i] || 0;
    const bVal = bParts[i] || 0;

    if (aVal !== bVal) {
      return aVal - bVal;
    }
  }
  return 0;
};

const categorizeVersionsByDeviceAndComponent = (versionsData) => {
  const G2TBU = [];
  const G2FS = [];
  const LTTBU = [];
  const LTFS = [];

  versionsData.forEach(item => {
    // Only include non-deleted items
    if (item.DeletedDateUtc == null) {
      const isG2 = item.DevType === deviceType.G2;
      const isLT = item.DevType === deviceType.LT;
      const isTBU = item.Component === 'TBU';
      const isFS = item.Component === 'FS';

      if (isTBU) {
        if (isG2) G2TBU.push(item);
        else if (isLT) LTTBU.push(item);
      } else if (isFS) {
        if (isG2) G2FS.push(item);
        else if (isLT) LTFS.push(item);
      }
    }
  });

  // Sort all arrays by version (newest to oldest)
  [G2TBU, G2FS, LTTBU, LTFS].forEach(arr => 
    arr.sort((a, b) => compareVersions(b.Version, a.Version))
  );

  return { G2TBU, G2FS, LTTBU, LTFS };
};

const AllSiteSettings = () => {
  const filterSites = () => {
    let filteredData = [];
    if (selectedSiteFilters.length > 0) {
      filteredData = [...allSitesData];
      filteredData = filteredData.filter(site => {
        return (
          (selectedSiteFilters.includes('Internal') || !site.IsInternal) &&
          ((selectedSiteFilters.includes('Gas') && !site.IsElectric) ||
          (selectedSiteFilters.includes('Electric') && site.IsElectric))
        );
      });
    }
    if (searchName.trim()) {
      filteredData = filteredData.filter(site =>
        (site.Name ?? '').toLowerCase().includes(searchName.trim().toLowerCase())
      );
    }
    if (searchVDU3PMID.trim()) {
      filteredData = filteredData.filter(site =>
        String(site.PackageManifestId ?? '').toLowerCase().includes(searchVDU3PMID.trim().toLowerCase())
      );
    }
    if (searchVDU4PMID.trim()) {
      filteredData = filteredData.filter(site =>
        String(site.V4ManifestSetId ?? '').toLowerCase().includes(searchVDU4PMID.trim().toLowerCase())
      );
    }
    if (selectedProductLevels.length > 0) {
      filteredData = filteredData.filter(site => selectedProductLevels.includes(site.ProductLevel));
    }
    if (selectedTimeZones.length > 0) {
      filteredData = filteredData.filter(site => selectedTimeZones.includes(site.TimeZone));
    }
    setSitesData(filteredData);
  };

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getPackageManifestsData, getPrivilegeAction } = useSelector((state) => state.commonReducers) || {};

  const [sitesData, setSitesData] = useState([]);
  const [allSitesData, setAllSitesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [selectedSiteFilters, setSelectedSiteFilters] = useState(['Gas', 'Electric']);
  const [showVDU2Columns, setShowVDU2Columns] = useState(true);
  const [showG2Columns, setShowG2Columns] = useState(true);
  const [showLTColumns, setShowLTColumns] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [searchVDU3PMID, setSearchVDU3PMID] = useState('');
  const [searchVDU4PMID, setSearchVDU4PMID] = useState('');
  const [selectedProductLevels, setSelectedProductLevels] = useState([]);
  const [showProductLevelDropdown, setShowProductLevelDropdown] = useState(false);
  const [selectedTimeZones, setSelectedTimeZones] = useState([]);
  const [showTimeZoneDropdown, setShowTimeZoneDropdown] = useState(false);
  const [availableTimeZones, setAvailableTimeZones] = useState([]);
  const [sitesPaginationModel, setSitesPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [editFormData, setEditFormData] = useState({
    ltSW: '',
    ltFS: '',
    ltOS: 0,
    g2SW: '',
    g2FS: '',
    g2OS: 0,
    vdu4PackageManifest: '',
    vdu3PackageManifest: ''
  });
  const [g2TBUVersions, setG2TBUVersions] = useState([]);
  const [g2FSVersions, setG2FSVersions] = useState([]);
  const [ltTBUVersions, setLTTBUVersions] = useState([]);
  const [ltFSVersions, setLTFSVersions] = useState([]);
  
  // Manifest modal states
  const [manifestModalOpen, setManifestModalOpen] = useState(false);
  const [manifestType, setManifestType] = useState(''); // 'vdu4' or 'vdu3'
  const [manifestSearchText, setManifestSearchText] = useState('');
  const [releaseVersionsOnly, setReleaseVersionsOnly] = useState(false);
  const [keepCarAssignments, setKeepCarAssignments] = useState(false);
  const [pendingSiteIds, setPendingSiteIds] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [clearCarAssignments, setClearCarAssignments] = useState(false);
  const privilege = "SWAssignment";
  useEffect(() => {
    fetchG2SoftwareData();
    fetchSitesData();
    dispatch(COMMON_SERVICES.getPackageManifestsServices());
    dispatch(COMMON_SERVICES.getPrivilegeServices(privilege));
  }, [dispatch]);

  useEffect(() => {
    filterSites();
  }, [allSitesData, selectedSiteFilters, searchName, searchVDU3PMID, searchVDU4PMID, selectedProductLevels, selectedTimeZones]);

  useEffect(() => {
    if (allSitesData.length > 0) {
      const uniqueTimeZones = [...new Set(allSitesData.map(item => item.TimeZone).filter(Boolean))].sort();
      setAvailableTimeZones(uniqueTimeZones);
    }
  }, [allSitesData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProductLevelDropdown && !event.target.closest('.product-level-dropdown-container')) {
        setShowProductLevelDropdown(false);
      }
      if (showTimeZoneDropdown && !event.target.closest('.timezone-dropdown-container')) {
        setShowTimeZoneDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductLevelDropdown, showTimeZoneDropdown]);

  const fetchG2SoftwareData = () => {
    setLoading(true);
    dispatch(COMMON_SERVICES.getAllG2SWInformationServices())
      .then((data) => {
        
        // Categorize versions for dropdowns
        const { G2TBU, G2FS, LTTBU, LTFS } = categorizeVersionsByDeviceAndComponent(data);
        setG2TBUVersions(G2TBU);
        setG2FSVersions(G2FS);
        setLTTBUVersions(LTTBU);
        setLTFSVersions(LTFS);
      })
      .catch((error) => {
        console.error("Error fetching G2 Software data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchSitesData = () => {
    setLoadingSites(true);
    dispatch(COMMON_SERVICES.getAdminSettingsForAllSitesServices())
      .then((data) => {
        const dataWithIds = data.map((item, index) => ({ ...item, id: item.Id || index + 1 }));
        setAllSitesData(dataWithIds);
        setSitesData(dataWithIds);
      })
      .catch((error) => {
        console.error("Error fetching sites data:", error);
      })
      .finally(() => {
        setLoadingSites(false);
      });
  };

  const handleFilterToggle = (setter) => (filter) => {
    setter(prev =>
      prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]
    );
  };
  const handleSiteFilterClick = handleFilterToggle(setSelectedSiteFilters);
  const productLevelOptions = useMemo(() => [
    { value: productLevel.VisageBasic, label: t("Visage_Basic") },
    { value: productLevel.VisageRT, label: t("Visage_RT") },
    { value: productLevel.VisageRTPlus, label: t("Visage_RT_Plus") },
    { value: productLevel.VisageTiered, label: t("Visage_Tiered") },
    { value: productLevel.VisageSharkExp, label: t("Visage_Shark_Experience") }
  ], [t]);

  const getProductLevelDisplayText = () => {
    if (selectedProductLevels.length === 0) return t("Product_Level_placeholder");
    if (selectedProductLevels.length === 1) {
      const option = productLevelOptions.find(opt => opt.value === selectedProductLevels[0]);
      return option ? option.label : "";
    }
    return `${selectedProductLevels.length} ${t("selected")}`;
  };

  const getTimeZoneDisplayText = () => {
    if (selectedTimeZones.length === 0) return t("Time_Zone_placeholder");
    if (selectedTimeZones.length === 1) return selectedTimeZones[0];
    return `${selectedTimeZones.length} ${t("selected")}`;
  };

  const handleProductLevelChange = (level) => {
    setSelectedProductLevels(prev =>
      prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]
    );
  };

  const handleTimeZoneChange = (timezone) => {
    setSelectedTimeZones(prev =>
      prev.includes(timezone) ? prev.filter(tz => tz !== timezone) : [...prev, timezone]
    );
  };

  const handleEditClick = (site) => {
    setSelectedSite(site);
    setEditFormData({
      ltSW: site.LTSWversion || '',
      ltFS: site.LTFSversion || '',
      ltOS: site.LTOSversion || 0,
      g2SW: site.G2SWversion || '',
      g2FS: site.G2FSversion || '',
      g2OS: site.G2OSversion || 0,
      vdu4PackageManifest: site.V4ManifestSetId || '',
      vdu3PackageManifest: site.PackageManifestId || ''
    });
    setEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
    setSelectedSite(null);
    setClearCarAssignments(false);
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveChanges = () => {
    if (!selectedSite) return;

    const data = {
      SiteId: selectedSite.SiteID || selectedSite.Id,
      GpsiCarId: null, // Set to null or specific carId if needed
      G2SW: editFormData.g2SW,
      G2OS: editFormData.g2OS,
      G2FS: editFormData.g2FS,
      LTSW: editFormData.ltSW,
      LTOS: editFormData.ltOS,
      LTFS: editFormData.ltFS,
      ResetCars: clearCarAssignments
    };

    dispatch(COMMON_SERVICES.setG2SoftwareVersionsServices(data))
      .then((response) => {
        // Optionally refresh the sites data
        fetchSitesData();
        handleCloseModal();
      })
      .catch((error) => {
        console.error('Error updating software versions:', error);
      });
  };

  const handleOpenManifestModal = (type) => {
    setManifestType(type);
    setManifestSearchText('');
    setReleaseVersionsOnly(false);
    setKeepCarAssignments(false);
    setManifestModalOpen(true);
  };

  const handleCloseManifestModal = () => {
    setManifestModalOpen(false);
    setManifestSearchText('');
    setReleaseVersionsOnly(false);
    setKeepCarAssignments(false);
  };

  const handleSelectManifest = (manifestId) => {
    // Call appropriate API based on manifest type
    const service = manifestType === 'vdu4' 
      ? COMMON_SERVICES.assignV4ManifestToSiteServices 
      : COMMON_SERVICES.assignManifestToSiteServices;
    
    dispatch(service(
      selectedSite.Id,
      manifestId,
      keepCarAssignments
    ))
    .then((response) => {
      if (response) {
        // Update form with selected manifest
        if (manifestType === 'vdu4') {
          handleFormChange('vdu4PackageManifest', manifestId);
        } else if (manifestType === 'vdu3') {
          handleFormChange('vdu3PackageManifest', manifestId);
        }
        handleCloseManifestModal();
      }
    })
    .catch((error) => {
      console.error("Error assigning manifest to site:", error);
    });
  };

  const getFilteredManifests = () => {
    if (!getPackageManifestsData || !Array.isArray(getPackageManifestsData)) {
      return [];
    }

    let filtered = [...getPackageManifestsData];

    // Filter by release versions only
    if (releaseVersionsOnly) {
      filtered = filtered.filter(item => item.ReleaseVersion === true);
    }

    // Filter by search text (name)
    if (manifestSearchText.trim()) {
      const searchLower = manifestSearchText.toLowerCase().trim();
      filtered = filtered.filter(item => 
        (item.ManifestDescription || '').toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  const getManifestDisplayText = (manifestId) => {
    if (!manifestId) return t("No_Manifest");
    
    if (!getPackageManifestsData || !Array.isArray(getPackageManifestsData)) {
      return `[${manifestId}]`;
    }

    const manifest = getPackageManifestsData.find(m => m.ManifestId == manifestId);
    
    if (!manifest) {
      return `[${manifestId}] ${t("Deleted")}?`;
    }

    return `[${manifest.ManifestId}] ${manifest.ManifestDescription || ''}`;
  };

  const baseSitesColumns = [
    { field: "Id", headerName: t("ID"), width: 60 },
    { 
      field: "Name", 
      headerName: t("Name"), 
      width: 200,
      renderCell: (params) => (
        <div 
          style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: 'var(--admin-tab-bg)' }}
          onClick={() => navigate(`/fleet?siteId=${params.row.Id}`)}
        >
          <span style={{textDecoration: 'underline',whiteSpace: 'normal'}}>{params.row.Name}</span>
          {pendingSiteIds.includes(params.row.Id) && (
            <DownloadIcon width="16px" height="16px" color="var(--admin-tab-bg)" />
          )}
        </div>
      )
    },
    { field: "TimeZone", headerName: t("Time_zone"), width: 200 },
    {
      field: "IsInternal",
      headerName: t("Internal"),
      width: 100,
      valueGetter: (params) => params.row.IsInternal ? t("Yes") : t("No")
    },
    {
      field: "IsElectric",
      headerName: t("Is_Electric"),
      width: 100,
      valueGetter: (params) => params.row.IsElectric ? t("Yes") : t("No")
    },
    {
      field: "ProductLevel",
      headerName: t("Product_Level"),
      width: 180,
      valueGetter: (params) => {
        const levelMap = {
          [productLevel.VisageBasic]: t("Visage_Basic"),
          [productLevel.VisageRT]: t("Visage_RT"),
          [productLevel.VisageRTPlus]: t("Visage_RT_Plus"),
          [productLevel.VisageTiered]: t("Visage_Tiered"),
          [productLevel.VisageSharkExp]: t("Visage_Shark_Experience")
        };
        return levelMap[params.row.ProductLevel] || t("Unknown");
      }
    },
    { field: "PackageManifestId", headerName: t("VDU3 PMID"), width: 100 },
    { field: "V4ManifestSetId", headerName: t("VDU4 PMID"), width: 100 }
  ];

  const actionsColumn = {
    field: "actions",
    headerName: t("Actions"),
    width: 80,
    renderCell: (params) => (
      <EditButton onClick={() => handleEditClick(params.row)}>
        <EditIcon />
      </EditButton>
    )
  };

  const vdu2Columns = [
    { field: "VduSWsystem", headerName: t("VDU_SW_sys"), width: 120 },
    { field: "Vdu20SWversion", headerName: t("2.0_SW"), width: 100 },
    { field: "Vdu20OSversion", headerName: t("2.0_OS"), width: 100 },
    { field: "Vdu21SWversion", headerName: t("2.1_SW"), width: 100 },
    { field: "Vdu21OSversion", headerName: t("2.1_OS"), width: 100 }
  ];

  const g2Columns = [
    { field: "G2SWversion", headerName: t("G2_TBU"), width: 100 },
    { field: "G2FSversion", headerName: t("G2_FS"), width: 100 },
    { field: "G2OSversion", headerName: t("G2_OS"), width: 100 }
  ];

  const ltColumns = [
    { field: "LTSWversion", headerName: t("LT_TBU"), width: 100 },
    { field: "LTFSversion", headerName: t("LT_FS"), width: 100 },
    { field: "LTOSversion", headerName: t("LT_OS"), width: 100 }
  ];

  const sitesColumns = [
    ...baseSitesColumns,
    ...(showVDU2Columns ? vdu2Columns : []),
    ...(showG2Columns ? g2Columns : []),
    ...(showLTColumns ? ltColumns : []),
    actionsColumn
  ];

  const handlePMIDPending = () => {
    setLoadingPending(true);
    dispatch(COMMON_SERVICES.getAllProgressPendingForTodayServices())
      .then((data) => {
        if (data && Array.isArray(data)) {
          const siteIds = data.map(item => item.SiteId || item.siteId).filter(Boolean);
          setPendingSiteIds(siteIds);
        }
      })
      .catch((error) => {
        console.error("Error fetching PMID pending data:", error);
      })
      .finally(() => {
        setLoadingPending(false);
      });
  };

  return (
    <AllSiteSettingsContainer>
      <TableContainer>
        <BodyWrapper>
          <ContentArea>
            <BlueAccordionStyled>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Heading>
                    <h3>{t("Sites")}</h3>
                  </Heading>
                </AccordionSummary>
                <AccordionDetails>
                  <WorkSection>
                    <FilterButtonGroup>
                      <FilterButton active={selectedSiteFilters.includes('Internal')} onClick={() => handleSiteFilterClick('Internal')}>{t("Internal")}</FilterButton>
                      <div style={{ display: 'flex', gap: 0 }}>
                        <FilterButton active={selectedSiteFilters.includes('Gas')} onClick={() => handleSiteFilterClick('Gas')} style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>{t("Gas")}</FilterButton>
                        <FilterButton active={selectedSiteFilters.includes('Electric')} onClick={() => handleSiteFilterClick('Electric')} style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, marginLeft: '-1px' }}>{t("Electric")}</FilterButton>
                      </div>
                      <SearchFilterContainer>
                        <SearchInput className="name-search" type="text" placeholder={t("Name_placeholder")} value={searchName} onChange={(e) => setSearchName(e.target.value)} />
                        {searchName && <ClearButton onClick={() => setSearchName('')}><CloseIcon width={12} height={12} /></ClearButton>}
                      </SearchFilterContainer>
                      <SearchFilterContainer>
                        <SearchInput className="pmid-search" type="text" placeholder={t("VDU3_PMID_placeholder")} value={searchVDU3PMID} onChange={(e) => setSearchVDU3PMID(e.target.value)} />
                        {searchVDU3PMID && <ClearButton onClick={() => setSearchVDU3PMID('')}><CloseIcon width={12} height={12} /></ClearButton>}
                      </SearchFilterContainer>
                      <SearchFilterContainer>
                        <SearchInput className="pmid-search" type="text" placeholder={t("VDU4_PMID_placeholder")} value={searchVDU4PMID} onChange={(e) => setSearchVDU4PMID(e.target.value)} />
                        {searchVDU4PMID && <ClearButton onClick={() => setSearchVDU4PMID('')}><CloseIcon width={12} height={12} /></ClearButton>}
                      </SearchFilterContainer>
                      <ProductLevelDropdownContainer className="product-level-dropdown-container">
                        <ProductLevelButton className="product-level-select" onClick={() => setShowProductLevelDropdown(!showProductLevelDropdown)} hasSelection={selectedProductLevels.length > 0}>
                          {getProductLevelDisplayText()}
                        </ProductLevelButton>
                        {selectedProductLevels.length > 0 && (
                          <ProductLevelClearButton onClick={(e) => { e.stopPropagation(); setSelectedProductLevels([]); }}>
                            <CloseIcon width={12} height={12} />
                          </ProductLevelClearButton>
                        )}
                        {showProductLevelDropdown && (
                          <ProductLevelDropdown>
                            {productLevelOptions.map(option => (
                              <ProductLevelOption key={option.value}>
                                <input type="checkbox" checked={selectedProductLevels.includes(option.value)} onChange={() => handleProductLevelChange(option.value)} />
                                {option.label}
                              </ProductLevelOption>
                            ))}
                            {selectedProductLevels.length > 0 && <ClearAllButton onClick={() => setSelectedProductLevels([])}>{t("Clear_All")}</ClearAllButton>}
                          </ProductLevelDropdown>
                        )}
                      </ProductLevelDropdownContainer>
                      <ProductLevelDropdownContainer className="timezone-dropdown-container">
                        <ProductLevelButton className="timezone-select" onClick={() => setShowTimeZoneDropdown(!showTimeZoneDropdown)} hasSelection={selectedTimeZones.length > 0}>
                          {getTimeZoneDisplayText()}
                        </ProductLevelButton>
                        {selectedTimeZones.length > 0 && (
                          <ProductLevelClearButton onClick={(e) => { e.stopPropagation(); setSelectedTimeZones([]); }}>
                            <CloseIcon width={12} height={12} />
                          </ProductLevelClearButton>
                        )}
                        {showTimeZoneDropdown && (
                          <ProductLevelDropdown>
                            {availableTimeZones.map(timezone => (
                              <ProductLevelOption key={timezone}>
                                <input type="checkbox" checked={selectedTimeZones.includes(timezone)} onChange={() => handleTimeZoneChange(timezone)} />
                                {timezone}
                              </ProductLevelOption>
                            ))}
                            {selectedTimeZones.length > 0 && <ClearAllButton onClick={() => setSelectedTimeZones([])}>{t("Clear_All")}</ClearAllButton>}
                          </ProductLevelDropdown>
                        )}
                      </ProductLevelDropdownContainer>
                      <DeleteToggleButton active={showVDU2Columns} onClick={() => setShowVDU2Columns(!showVDU2Columns)}>{t("VDU2")}</DeleteToggleButton>
                      <DeleteToggleButton active={showG2Columns} onClick={() => setShowG2Columns(!showG2Columns)}>{t("G2")}</DeleteToggleButton>
                      <DeleteToggleButton active={showLTColumns} onClick={() => setShowLTColumns(!showLTColumns)}>{t("LT")}</DeleteToggleButton>
                      <FilterButton active={pendingSiteIds.length > 0} onClick={handlePMIDPending} disabled={loadingPending} style={{ display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
                        <RefreshIcon />
                        <span>{loadingPending ? t("Loading") : t("PMID_Pending")}</span>
                      </FilterButton>
                    </FilterButtonGroup>

                    {loadingSites ? (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <Loader />
                      </div>
                    ) : (
                      <div style={{ height: 500, width: '100%', overflow: 'auto' }}>
                        <DataGrid
                          rows={sitesData}
                          columns={sitesColumns}
                          paginationModel={sitesPaginationModel}
                          onPaginationModelChange={setSitesPaginationModel}
                          pageSizeOptions={[10, 25, 50]}
                          disableColumnMenu
                          autoHeight={false}
                          sx={{ width: 'fit-content', minWidth: '100%' }}
                        />
                      </div>
                    )}

                  </WorkSection>
                </AccordionDetails>
              </Accordion>
            </BlueAccordionStyled>
          </ContentArea>
        </BodyWrapper>
      </TableContainer>

      <Modal
        open={editModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="edit-site-modal"
      >
        <ModalBox>
          <ModalHeader>
            <h2>{t("Edit_Site_Settings")}</h2>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon width={20} height={20} />
            </IconButton>
          </ModalHeader>

          <ModalContent>
            {selectedSite && (
              <>
                <ModalSection>
                  <SectionTitle>{t("Site_Software_Assignments")}</SectionTitle>
                  <SectionContent>
                    <InfoRow>
                      <strong>{t("Site_ID")}:</strong>
                      <span>{selectedSite.Id}</span>
                    </InfoRow>
                    <InfoRow>
                      <strong>{t("Name")}:</strong>
                      <span>{selectedSite.Name}</span>
                    </InfoRow>
                  </SectionContent>
                </ModalSection>

                <ModalSection>
                  <SectionTitle>{t("LT_G2_Versions")}</SectionTitle>
                  <SectionContent>
                    <FormRow>
                      <FormField>
                        <Label>{t("LT_SW")}</Label>
                        <FormControl fullWidth size="small">
                          <Select
                            value={editFormData.ltSW}
                            onChange={(e) => handleFormChange('ltSW', e.target.value)}
                          >
                            <MenuItem value="">{t("Select")}</MenuItem>
                            {
                              ltTBUVersions.map((version, index) => (
                                <MenuItem key={index} value={version?.Version || ''}>
                                  {version?.Version || ''} {version?.Description ? `- ${version.Description}` : ''}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </FormField>

                      <FormField>
                        <Label>{t("LT_FS")}</Label>
                        <FormControl fullWidth size="small">
                          <Select
                            value={editFormData.ltFS}
                            onChange={(e) => handleFormChange('ltFS', e.target.value)}
                          >
                            <MenuItem value="">{t("Select")}</MenuItem>
                              {ltFSVersions.map((version, index) => (
                                <MenuItem key={index} value={version?.Version || ''}>
                                  {version?.Version || ''} {version?.Description ? `- ${version.Description}` : ''}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </FormField>

                      <FormField>
                        <Label>{t("LT_OS")}</Label>
                        <NumberInput
                          type="number"
                          value={editFormData.ltOS}
                          onChange={(e) => handleFormChange('ltOS', parseInt(e.target.value) || 0)}
                          min="0"
                        />
                      </FormField>
                    </FormRow>

                    <FormRow>
                      <FormField>
                        <Label>{t("G2_SW")}</Label>
                        <FormControl fullWidth size="small">
                          <Select
                            value={editFormData.g2SW}
                            onChange={(e) => handleFormChange('g2SW', e.target.value)}
                          >
                            <MenuItem value="">{t("Select")}</MenuItem>
                              {g2TBUVersions.map((version, index) => (
                                <MenuItem key={index} value={version?.Version || ''}>
                                  {version?.Version || ''} {version?.Description ? `- ${version.Description}` : ''}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </FormField>

                      <FormField>
                        <Label>{t("G2_FS")}</Label>
                        <FormControl fullWidth size="small">
                          <Select
                            value={editFormData.g2FS}
                            onChange={(e) => handleFormChange('g2FS', e.target.value)}
                          >
                            <MenuItem value="">{t("Select")}</MenuItem>
                              {g2FSVersions.map((version, index) => (
                                <MenuItem key={index} value={version?.Version || ''}>
                                  {version?.Version || ''} {version?.Description ? `- ${version.Description}` : ''}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </FormField>

                      <FormField>
                        <Label>{t("G2_OS")}</Label>
                        <NumberInput
                          type="number"
                          value={editFormData.g2OS}
                          onChange={(e) => handleFormChange('g2OS', parseInt(e.target.value) || 0)}
                          min="0"
                        />
                      </FormField>
                    </FormRow>
                  </SectionContent>
                </ModalSection>

                <ModalSection>
                  <SectionTitle>{t("Android_Package_Manifest")}</SectionTitle>
                  <SectionContent>
                    <FormRow>
                      <FormField>
                        <Label>{t("VDU_4x")}</Label>
                        <Button onClick={() => handleOpenManifestModal('vdu4')}>
                          {getManifestDisplayText(editFormData.vdu4PackageManifest)}
                        </Button>
                      </FormField>

                      <FormField>
                        <Label>{t("VDU_3x")}</Label>
                        <Button onClick={() => handleOpenManifestModal('vdu3')}>
                          {getManifestDisplayText(editFormData.vdu3PackageManifest)}
                        </Button>
                      </FormField>
                    </FormRow>
                  </SectionContent>
                </ModalSection>
              </>
            )}
          </ModalContent>

          <ModalFooter>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clearCarAssignments}
                  onChange={(e) => setClearCarAssignments(e.target.checked)}
                />
              }
              label={t("Clear_Car_Specific_Assignments")}
              style={{ marginRight: 'auto' }}
            />
            <ModalButton className="secondary" onClick={handleCloseModal}>
              {t("Cancel")}
            </ModalButton>
              <ModalButton 
                className="primary" 
                onClick={handleSaveChanges}
                disabled={!(getPrivilegeAction && getPrivilegeAction[privilege])}
              >
                {t("Save")}
              </ModalButton>
          </ModalFooter>
        </ModalBox>
      </Modal>

      {/* Manifest Selection Modal */}
      <Modal
        open={manifestModalOpen}
        onClose={handleCloseManifestModal}
        aria-labelledby="manifest-modal"
      >
        <ModalBox>
          <ModalHeader>
            <h2>{manifestType === 'vdu4' ? t("VDU_4x") : t("VDU_3x")} - {t("Select_Manifest")}</h2>
            <IconButton onClick={handleCloseManifestModal} size="small">
              <CloseIcon width={20} height={20} />
            </IconButton>
          </ModalHeader>
          
          <ModalContent>
            <ModalSection>
              <SectionContent>
                <FormRow style={{ marginBottom: '16px', alignItems: 'center' }}>
                  <SearchFilterContainer style={{ marginRight: '12px', maxWidth: '300px' }}>
                    <SearchInput 
                      type="text" 
                      placeholder={t("Search_by_name")} 
                      value={manifestSearchText} 
                      onChange={(e) => setManifestSearchText(e.target.value)}
                      style={{ width: '100%' }}
                    />
                    {manifestSearchText && (
                      <ClearButton onClick={() => setManifestSearchText('')}>
                        <CloseIcon width={12} height={12} />
                      </ClearButton>
                    )}
                  </SearchFilterContainer>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={releaseVersionsOnly}
                        onChange={(e) => setReleaseVersionsOnly(e.target.checked)}
                      />
                    }
                    label={t("Release_Versions_Only")}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={keepCarAssignments}
                        onChange={(e) => setKeepCarAssignments(e.target.checked)}
                      />
                    }
                    label={t("Keep_Car_Assignments")}
                  />
                </FormRow>
                <div style={{ height: 400, width: '100%' }}>
                  <DataGrid
                    rows={getFilteredManifests().map((item, index) => ({ ...item, id: item.ManifestId || index }))}
                    columns={[
                      { 
                        field: "ManifestId", 
                        headerName: t("ID"), 
                        width: 100 
                      },
                      { 
                        field: "ManifestDescription", 
                        headerName: t("Name"), 
                        flex: 1, 
                        minWidth: 300 
                      },
                      { 
                        field: "CreationDate", 
                        headerName: t("Created"), 
                        width: 180 
                      },
                      {
                        field: "actions",
                        headerName: t("Actions"),
                        width: 120,
                        renderCell: (params) => (
                            <Button 
                              onClick={() => handleSelectManifest(params.row.ManifestId)}
                              disabled={!(getPrivilegeAction && getPrivilegeAction[privilege])}
                            >
                              {t("Select")}
                            </Button>
                        )
                      }
                    ]}
                    pageSizeOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    disableColumnMenu
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } }
                    }}
                  />
                </div>
              </SectionContent>
            </ModalSection>
          </ModalContent>

          <ModalFooter>
            <ModalButton className="secondary" onClick={handleCloseManifestModal}>
              {t("Close")}
            </ModalButton>
          </ModalFooter>
        </ModalBox>
      </Modal>
    </AllSiteSettingsContainer>
  );
};

export default AllSiteSettings;
